import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Alert, Typography } from "@mui/material";

/**
 * WTH — Auth0 OAuth callback handler.
 *
 * Auth0 redirects here after the user authenticates. We POST the `code`
 * to the backend, which exchanges it for tokens, validates them, upserts
 * the local User row, and sets our httpOnly session cookies. We then
 * route to `/`, where the existing app bootstrap hits /auth/me and
 * hydrates the auth store as it always does on first load — no
 * manual setUser() needed here.
 */
export default function Auth0CallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // One-time guard for the Auth0 code-exchange. `useSearchParams` returns a
  // fresh `URLSearchParams` instance on every render, so depending on `params`
  // would re-fire the effect (and re-POST the code) on every parent re-render
  // — even though the underlying query string hasn't changed. Authorization
  // codes are single-use; the 2nd POST always 400s with "invalid_grant" and
  // the user sees a spurious "Login failed" toast. The ref flips to `true`
  // synchronously before the async IIFE starts so even StrictMode's double-
  // invocation in dev only triggers one network request.
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    const code = params.get("code");
    const errParam = params.get("error");
    if (errParam) {
      setError(params.get("error_description") || errParam);
      return;
    }
    if (!code) {
      setError("Missing authorization code");
      return;
    }
    exchanged.current = true;

    (async () => {
      try {
        const base = import.meta.env.VITE_API_BASE_URL || "";
        const resp = await fetch(`${base}/auth/auth0/callback`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        });
        if (!resp.ok) {
          const body = await resp.text();
          throw new Error(`Auth0 callback failed: ${resp.status} — ${body}`);
        }
        await resp.json();
        // Session cookies are set by the backend. The app bootstrap reads
        // /auth/me on first protected-route render and hydrates the store.
        navigate("/", { replace: true });
      } catch (e: any) {
        setError(e?.message || "Login failed");
      }
    })();
  }, [params, navigate]);

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 480, mx: "auto" }}>
        <Alert severity="error">{error}</Alert>
        <Typography
          sx={{ mt: 2, cursor: "pointer", color: "primary.main" }}
          onClick={() => navigate("/login")}
        >
          Back to login
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
      <CircularProgress />
      <Typography sx={{ mt: 2 }}>Finishing sign-in…</Typography>
    </Box>
  );
}
